import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import * as Icons from "lucide-react";
import { images } from "../mock";

export const LaptopPurchase = () => {
  const laptopTypes = [
    {
      title: "Business-Laptops",
      description: "Hochwertige Geschäftslaptops von Dell, Lenovo, HP und anderen Premium-Herstellern",
      icon: "Laptop",
    },
    {
      title: "Leasing-Rückläufer",
      description: "Geräte aus ausgelaufenen Leasing-Verträgen in größeren Mengen",
      icon: "RefreshCw",
    },
    {
      title: "IT-Refresh Hardware",
      description: "Laptops aus Hardware-Refresh-Projekten und IT-Modernisierungen",
      icon: "Sparkles",
    },
    {
      title: "Großgerätebestände",
      description: "Komplette Gerätebestände bei Unternehmensauflösungen oder -umzügen",
      icon: "Package",
    },
  ];

  const manufacturers = [
    { name: "Dell", models: "Latitude, Precision, XPS" },
    { name: "Lenovo", models: "ThinkPad, ThinkBook" },
    { name: "HP", models: "EliteBook, ProBook, ZBook" },
    { name: "Apple", models: "MacBook Pro, MacBook Air" },
    { name: "Fujitsu", models: "Lifebook" },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Firmenlaptop-Ankauf
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Wir kaufen professionell gebrauchte Business-Laptops in größeren Mengen an. 
              Egal ob Leasing-Rückläufer, IT-Refresh oder Unternehmensauflösung – wir bieten 
              faire Preise und eine unkomplizierte Abwicklung.
            </p>
          </div>
        </div>
      </section>

      {/* Laptop Types Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Was wir ankaufen
            </h2>
            <p className="text-xl text-gray-600">
              Verschiedene Kategorien von Business-Laptops
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {laptopTypes.map((type, index) => {
              const Icon = Icons[type.icon];
              return (
                <Card key={index} className="border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {type.title}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {type.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <img
              src={images.multipleLaptops}
              alt="Multiple Business Laptops"
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Manufacturers Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Alle Marken im Ankauf
            </h2>
            <p className="text-xl text-gray-600">
              Wir kaufen Business-Laptops aller führenden Hersteller
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {manufacturers.map((manufacturer, index) => (
              <Card key={index} className="border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-300">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-16 bg-gray-50 rounded-lg flex items-center justify-center">
                      {manufacturer.name === "Apple" ? (
                        <Icons.Apple className="text-gray-900" size={32} strokeWidth={1.5} />
                      ) : (
                        <div className={`text-xl font-bold ${
                          manufacturer.name === "Dell" ? "text-[#007DB8]" :
                          manufacturer.name === "Lenovo" ? "text-[#E2231A]" :
                          manufacturer.name === "HP" ? "text-[#0096D6]" :
                          manufacturer.name === "Fujitsu" ? "text-[#FF0000]" :
                          "text-gray-900"
                        }`}>
                          {manufacturer.displayName}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {manufacturer.name}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Business Laptops
                      </p>
                    </div>
                  </div>
                  <Icons.CheckCircle2 className="text-blue-600" size={24} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Bereit für Ihr Angebot?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Fordern Sie jetzt Ihre kostenlose Bewertung an
            </p>
            <Link to="/request-quote">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-medium shadow-lg">
                Jetzt Angebot anfragen
                <Icons.ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
